import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { api, formatApiError } from "../../lib/api";
import { ROLE_LABELS } from "../../lib/labels";
import { SecurityActionDialog } from "./SecurityActionDialog";
import { RiskBadge, StatusBadge, wib } from "./badges";

const Field = ({ k, v, testid }) => (
  <div className="flex justify-between gap-3 py-1 text-sm"><dt className="text-slate-500">{k}</dt><dd className="font-medium text-slate-900 text-right break-all" data-testid={testid}>{v || "-"}</dd></div>
);

const Evidence = ({ items }) => (
  <ul className="space-y-1 text-xs text-slate-700" data-testid="alert-evidence">
    {items.map((e, i) => (
      <li key={i} className="rounded bg-slate-50 border border-slate-100 p-2 break-all">
        {wib(e.at)} · IP {e.ip || "-"} · {Object.entries(e).filter(([k]) => !["at", "ip"].includes(k)).map(([k, v]) => `${k}: ${v}`).join(" · ")}
      </li>
    ))}
  </ul>
);

export const AlertDetail = ({ alertId, userId, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [action, setAction] = useState(null);
  const load = useCallback(() => {
    const url = alertId ? `/admin/security/alerts/${alertId}` : `/admin/security/users/${userId}`;
    api.get(url).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [alertId, userId]);
  useEffect(() => {
    if (alertId || userId) load();
    else setD(null);
  }, [alertId, userId, load]);

  const acc = d?.account;
  const uid = d?.userId || userId;
  const done = () => { setAction(null); load(); onChanged(); };
  return (
    <Sheet open={!!(alertId || userId)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="alert-detail">
        <SheetTitle>{d?.title || "Detail Akun"}</SheetTitle>
        {d && (
          <div className="mt-4 space-y-5">
            {alertId && (
              <div className="flex flex-wrap gap-2 items-center"><RiskBadge risk={d.risk} /><StatusBadge status={d.status} /><span className="text-xs text-slate-500">{d.count}x · pertama {wib(d.createdAt)} · terakhir {wib(d.lastSeenAt)}</span></div>
            )}
            <dl className="rounded-lg border border-slate-200 p-3">
              <Field k="Nama akun" v={acc?.name || d.userName} testid="alert-user-name" />
              <Field k="ID akun" v={uid} testid="alert-user-id" />
              <Field k="Email" v={acc?.email || d.userEmail} />
              <Field k="Peran" v={ROLE_LABELS[acc?.role || d.userRole] || acc?.role} />
              <Field k="Organisasi" v={d.orgName} />
              <Field k="Status akun" v={acc?.status} testid="alert-user-status" />
              <Field k="Sesi aktif / perangkat terpercaya" v={acc ? `${d.sessions.length} / ${d.trustedDevices}` : null} />
            </dl>
            {alertId && d.criticalEmailedAt && <p className="text-xs text-red-800" data-testid="alert-critical-email">Email peringatan KRITIS dikirim ke kontak keamanan: {wib(d.criticalEmailedAt)}{d.criticalEmail ? ` (${Object.values(d.criticalEmail).map((v) => (v === "SENT" ? "terkirim" : "antre kirim ulang")).join(", ")})` : ""}</p>}
            {alertId && <section><p className="text-xs font-bold uppercase text-slate-500 mb-1">Ringkasan bukti</p><Evidence items={d.evidence || []} /></section>}
            {acc && !d.protected && (
              <div className="flex flex-wrap gap-2" data-testid="alert-actions">
                <Button size="sm" variant="outline" onClick={() => setAction("revoke-sessions")} data-testid="action-revoke-sessions">Cabut Semua Sesi</Button>
                <Button size="sm" variant="outline" onClick={() => setAction("require-reverify")} data-testid="action-require-reverify">Minta Verifikasi Ulang</Button>
                {acc.status === "ACTIVE" && <Button size="sm" className="bg-red-700 hover:bg-red-800" onClick={() => setAction("deactivate")} data-testid="action-deactivate">Nonaktifkan Akun</Button>}
                {acc.status === "INACTIVE" && <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900" onClick={() => setAction("reactivate")} data-testid="action-reactivate">Aktifkan Kembali</Button>}
              </div>
            )}
            {d.protected && <p className="text-xs text-amber-800" data-testid="alert-protected">Akun Owner Utama / akun Anda sendiri tidak dapat dikenai tindakan dari Pusat Keamanan.</p>}
            {alertId && d.status !== "DITANGANI" && (
              <div className="flex gap-2">
                {d.status === "BARU" && <Button size="sm" variant="outline" onClick={() => setAction("DITINJAU")} data-testid="action-mark-reviewed">Tandai Ditinjau</Button>}
                <Button size="sm" variant="outline" onClick={() => setAction("DITANGANI")} data-testid="action-mark-handled">Tandai Ditangani</Button>
              </div>
            )}
            {alertId && (
              <section><p className="text-xs font-bold uppercase text-slate-500 mb-1">Riwayat tindakan</p>
                <ul className="space-y-1 text-xs" data-testid="alert-action-history">
                  {!d.actions?.length && <li className="text-slate-500">Belum ada tindakan.</li>}
                  {d.actions?.map((a) => <li key={a.id}><b>{a.action}</b> oleh {a.byName} · {wib(a.at)} — {a.note}</li>)}
                </ul>
              </section>
            )}
            {acc && (
              <section><p className="text-xs font-bold uppercase text-slate-500 mb-1">Aktivitas akun terbaru</p>
                <ul className="space-y-0.5 text-xs text-slate-700 max-h-60 overflow-y-auto" data-testid="alert-account-activity">
                  {d.activity.map((a, i) => <li key={i}>{wib(a.createdAt)} · {a.action} · IP {a.ip || "-"}</li>)}
                </ul>
              </section>
            )}
          </div>
        )}
        <SecurityActionDialog action={action} userId={uid} alertId={alertId} onClose={() => setAction(null)} onDone={done} />
      </SheetContent>
    </Sheet>
  );
};
